/** Anything react (incl. react-hook-form's `field.ref`) may hand a `ref` prop. */
type RefTarget =
    | ((el: HTMLElement | null) => void)
    | { current: HTMLElement | null }
    | null
    | undefined;

type ComboboxCallbacks = {
    onChange?: (value: string) => void;
    onSelect?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: (value: string) => void;
    /** Forwarded so react-hook-form's `field.ref` still receives the element. */
    forwardRef?: RefTarget;
    /**
     * <w-combobox> has no `autofocus` attribute, so focus it imperatively once
     * the host shell has upgraded the element (it registers at runtime).
     */
    autoFocus?: boolean;
};

const assignRef = (ref: RefTarget, el: HTMLElement | null) => {
    if (typeof ref === 'function') ref(el);
    else if (ref && typeof ref === 'object') ref.current = el;
};

// The inline `ref={bindCombobox({...})}` callback gets a fresh identity every render, so
// React re-runs it on each commit. Track which elements were already auto-focused so we
// focus once on mount (matching the old `autoFocus` prop) instead of stealing focus back
// on every re-render.
const autoFocused = new WeakSet<HTMLElement>();

/**
 * Wires the <w-combobox> web component's CustomEvents to React callbacks.
 *
 * React's synthetic `on*` handlers do NOT fire on custom elements, and
 * <w-combobox> emits `change`/`select`/`blur` as `CustomEvent<{ value: string }>`
 * and `focus` as a bare CustomEvent (only when `open-on-focus` is set). So we
 * attach native listeners via a callback ref and unwrap `detail.value`.
 */
export const bindCombobox =
    ({ onChange, onSelect, onFocus, onBlur, forwardRef, autoFocus }: ComboboxCallbacks) =>
    (el: HTMLElement | null) => {
        assignRef(forwardRef, el);
        if (!el) return;

        if (autoFocus && !autoFocused.has(el)) {
            autoFocused.add(el);
            void customElements.whenDefined(el.localName).then(() => el.focus());
        }

        const handleChange = (e: Event) =>
            onChange?.((e as CustomEvent<{ value: string }>).detail.value);
        const handleSelect = (e: Event) =>
            onSelect?.((e as CustomEvent<{ value: string }>).detail.value);
        const handleFocus = () => onFocus?.();
        const handleBlur = (e: Event) =>
            onBlur?.((e as CustomEvent<{ value: string }>).detail.value);

        el.addEventListener('change', handleChange);
        el.addEventListener('select', handleSelect);
        el.addEventListener('focus', handleFocus);
        el.addEventListener('blur', handleBlur);

        // React 19 runs this cleanup on unmount / ref change.
        return () => {
            el.removeEventListener('change', handleChange);
            el.removeEventListener('select', handleSelect);
            el.removeEventListener('focus', handleFocus);
            el.removeEventListener('blur', handleBlur);
            assignRef(forwardRef, null);
        };
    };
